import type SubtitleCue from './subtitleCue';
import type TranscriptWord from './transcriptWord';
import type {
  TranscriptSliderRuleSetting,
  TranscriptBooleanRuleSetting,
} from './transcriptRuleSetting';

export type ParametricRuleCheckCallback = (
  currentSubtitleCue: SubtitleCue,
  prev: TranscriptWord,
  cur: TranscriptWord,
) => boolean;

export type ToStringCallback = (value: number) => string;

/*
  This module is the runtime "behavior" layer for transcript breaking rules. A
  TranscriptParametricRule is the executable form of a TranscriptRuleSetting (the
  serializable config in ./transcriptRuleSetting): the same setting data, derived
  via intersection so the data shape stays single-sourced, plus in-memory
  callbacks. Those function fields make this type non-serializable — persist and
  transport TranscriptRuleSetting instead. Instances are built from settings by
  getParametricRules in ./transcriptParametricRules.
*/

/**
 * The evaluation behavior shared by every rule: the predicate that decides
 * whether to break a paragraph/subtitle at a given word boundary.
 */
interface TranscriptRuleBehavior {
  checkRule: ParametricRuleCheckCallback;
}

/*
  Slider rule: a numeric-threshold setting enriched with its evaluation
  predicate and a formatter for the value shown in the UI. `formattedValue` is
  always populated alongside the slider data fields it inherits from the setting.
*/
export type TranscriptParametricSliderRule = TranscriptSliderRuleSetting & TranscriptRuleBehavior & {
  formattedValue: ToStringCallback;
};

/*
  Boolean rule: a toggle setting enriched only with its evaluation predicate. It
  has no numeric threshold, so its checkRule needs no value. `formattedValue` is
  disallowed so the union discriminates cleanly.
*/
type TranscriptParametricBooleanRule = TranscriptBooleanRuleSetting & TranscriptRuleBehavior & {
  formattedValue?: never;
};

type TranscriptParametricRule = TranscriptParametricSliderRule | TranscriptParametricBooleanRule;

/**
 * Returns whether a parametric rule includes slider fields (`value`, `min`, `max`, and `formattedValue`).
 */
export function isTranscriptParametricSliderRule(rule: TranscriptParametricRule): rule is TranscriptParametricSliderRule {
  if (!('value' in rule) || !('min' in rule) || !('max' in rule) || !('formattedValue' in rule)) {
    return false;
  }

  return typeof rule.value === 'number'
    && typeof rule.min === 'number'
    && typeof rule.max === 'number'
    && typeof rule.formattedValue === 'function';
}

export default TranscriptParametricRule;
